import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { adminAPI } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Users, 
  Building2, 
  TrendingUp, 
  Wallet,
  AlertCircle,
  ChevronRight,
  Shield,
  FileText,
  Clock,
  CheckCircle2,
  Loader2,
  FileSignature,
  Pen
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  const [stats, setStats] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, signaturesRes, commitmentsRes] = await Promise.all([
        adminAPI.getStats(),
        api.get('/admin/signature-requests?limit=100').catch(() => ({ data: { requests: [] } })),
        api.get('/admin/commitments?limit=100').catch(() => ({ data: { commitments: [] } }))
      ]);
      setStats(statsRes.data);
      setSignatures(signaturesRes.data.requests || []);
      setCommitments(commitmentsRes.data.commitments || []);
    } catch (error) {
      setStats({
        users: { total: 0, investors: 0, pending_verifications: 0 },
        opportunities: { total: 0, open: 0 },
        properties: { total: 0, pending_review: 0 },
        commitments: { total: 0, pending: 0, capital_by_currency: {} }
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate signature stats
  const signatureStats = {
    pending: signatures.filter(s => ['pending', 'viewed'].includes(s.status)).length,
    signed: signatures.filter(s => s.status === 'signed').length,
    completed: signatures.filter(s => s.status === 'completed').length,
    rejected: signatures.filter(s => s.status === 'rejected').length
  };

  // Calculate commitment pipeline
  const commitmentPipeline = {
    awaiting_signature: commitments.filter(c => c.status === 'awaiting_signature').length,
    countersign_pending: commitments.filter(c => c.status === 'countersign_pending').length,
    approved: commitments.filter(c => c.status === 'approved').length,
    funds_pending: commitments.filter(c => c.status === 'funds_pending').length,
    confirmed: commitments.filter(c => c.status === 'confirmed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Usuarios Totales',
      value: stats?.users?.total || 0,
      icon: Users,
      link: '/admin/users',
      color: 'text-primary'
    },
    {
      title: 'Inversionistas',
      value: stats?.users?.investors || 0,
      icon: TrendingUp,
      link: '/admin/users?role=investor',
      color: 'text-emerald-500'
    },
    {
      title: 'Verificaciones Pendientes',
      value: stats?.users?.pending_verifications || 0,
      icon: Clock,
      link: '/admin/compliance',
      color: 'text-amber-500',
      alert: true
    },
    {
      title: 'Oportunidades Abiertas',
      value: stats?.opportunities?.open || 0,
      icon: Building2,
      link: '/admin/opportunities',
      color: 'text-blue-500'
    },
    {
      title: 'Inmuebles en Revisión',
      value: stats?.properties?.pending_review || 0,
      icon: FileText,
      link: '/admin/properties',
      color: 'text-purple-500',
      alert: true
    },
    {
      title: 'Compromisos Pendientes',
      value: stats?.commitments?.pending || 0,
      icon: Wallet,
      link: '/admin/commitments',
      color: 'text-orange-500',
      alert: true
    },
  ];

  const totalCapitalMXN = stats?.commitments?.capital_by_currency?.MXN || 0;
  const totalCapitalUSD = stats?.commitments?.capital_by_currency?.USD || 0;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Panel de Administración</h1>
          <p className="body-text">
            Bienvenido, {user?.name}. Aquí tiene un resumen de la actividad de la plataforma.
          </p>
        </div>
        <Badge variant="outline" className="flex items-center gap-2">
          <Shield className="h-3 w-3" strokeWidth={1.5} />
          {user?.role?.replace('_', ' ').toUpperCase()}
        </Badge>
      </div>

      {/* Alerts */}
      {(stats?.users?.pending_verifications > 0 || stats?.properties?.pending_review > 0) && (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-medium text-amber-500 mb-2">Acciones Pendientes</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {stats?.users?.pending_verifications > 0 && (
                    <li>• {stats.users.pending_verifications} verificaciones de usuario pendientes</li>
                  )}
                  {stats?.properties?.pending_review > 0 && (
                    <li>• {stats.properties.pending_review} inmuebles esperando revisión</li>
                  )}
                  {stats?.commitments?.pending > 0 && (
                    <li>• {stats.commitments.pending} compromisos de inversión pendientes</li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capital Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-dashboard bg-gradient-to-br from-primary/10 to-transparent">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Capital Comprometido (MXN)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-light">{formatCurrency(totalCapitalMXN, 'MXN')}</p>
          </CardContent>
        </Card>
        <Card className="card-dashboard bg-gradient-to-br from-emerald-500/10 to-transparent">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Capital Comprometido (USD)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-light">{formatCurrency(totalCapitalUSD, 'USD')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className={`card-dashboard hover:border-primary/30 transition-colors ${stat.alert && stat.value > 0 ? 'border-amber-500/30' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">{stat.title}</p>
                      <p className="text-3xl font-light">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-sm bg-muted ${stat.color}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Signature & Commitment Pipeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Signature Pipeline */}
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSignature className="h-4 w-4 text-primary" />
              Pipeline de Firmas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pendientes</span>
                <Badge variant="outline" className={signatureStats.pending > 0 ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : ''}>
                  {signatureStats.pending}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Firmados (Por Verificar)</span>
                <Badge variant="outline" className={signatureStats.signed > 0 ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : ''}>
                  {signatureStats.signed}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Completados</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                  {signatureStats.completed}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Rechazados</span>
                <Badge variant="outline" className={signatureStats.rejected > 0 ? 'bg-red-500/10 text-red-500 border-red-500/20' : ''}>
                  {signatureStats.rejected}
                </Badge>
              </div>
            </div>
            <Link to="/admin/signatures" className="block mt-4">
              <Button variant="outline" size="sm" className="w-full gap-2">
                <Pen className="h-3 w-3" />
                Gestionar Firmas
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Commitment Pipeline */}
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="h-4 w-4 text-primary" />
              Pipeline de Compromisos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Esperando Firma</span>
                <Badge variant="outline" className={commitmentPipeline.awaiting_signature > 0 ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : ''}>
                  {commitmentPipeline.awaiting_signature}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Contrafirma Pendiente</span>
                <Badge variant="outline" className={commitmentPipeline.countersign_pending > 0 ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : ''}>
                  {commitmentPipeline.countersign_pending}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Aprobados</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                  {commitmentPipeline.approved}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Fondos Pendientes</span>
                <Badge variant="outline" className={commitmentPipeline.funds_pending > 0 ? 'bg-orange-500/10 text-orange-500 border-orange-500/20' : ''}>
                  {commitmentPipeline.funds_pending}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Confirmados</span>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                  {commitmentPipeline.confirmed}
                </Badge>
              </div>
            </div>
            <Link to="/admin/commitments" className="block mt-4">
              <Button variant="outline" size="sm" className="w-full gap-2">
                <Wallet className="h-3 w-3" />
                Gestionar Compromisos
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/opportunities/new" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Crear Nueva Oportunidad</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
            <Link to="/admin/compliance" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Revisar Verificaciones</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
            <Link to="/admin/properties" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Revisar Inmuebles</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
            <Link to="/admin/audit-log" className="flex items-center justify-between p-3 rounded-sm hover:bg-muted transition-colors">
              <span className="text-sm">Ver Registro de Auditoría</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </Link>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Estado del Sistema</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">API Backend</span>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Operativo
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Base de Datos</span>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Operativo
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Almacenamiento</span>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Operativo
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Autenticación</span>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Operativo
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
